export interface PublicOutcomeSummary {
  id: string;
  slug?: string;
  title: string;
  description?: string;
  plain_summary?: string;
  discipline?: string;
  is_public: boolean;
  price_cents?: number;
  downloads?: number;
  author?: string;
  author_initials?: string;
  quality_score?: number;
  contract?: {
    inputs?: Array<{ name: string; type: string }>;
    outputs?: Array<{ name: string; type: string }>;
    rubric?: Array<{ id: string; name: string; max_points: number }>;
  };
  // Mirrors store.OutcomeComposition. The API sends the steps themselves, not a
  // precomputed count.
  composition?: {
    nodes?: Array<{ id: string; skill_id: string }>;
    edges?: Array<{ id: string; from: string; to: string }>;
    pipeline_steps?: Array<{ id: string; skill_id: string }>;
  };
  pipeline_steps?: Array<{ id: string; skill_id: string }>;
  // Mirrors store.OutcomePlaybook.
  playbook?: {
    overview?: string;
    rules?: string[];
    heuristics?: string[];
    failure_modes?: string[];
  };
  score?: {
    overall?: number;
    checks?: Array<{ criterion_id: string; passed: boolean; score: number; reason: string }>;
    run_id?: string;
    model_benchmarks?: Array<{ model: string; score: number; latency_ms?: number }>;
  };
  context?: Array<{
    name: string;
    kind: string;
    description?: string;
    required?: boolean;
    example?: string;
  }>;
  test_rig?: {
    epochs?: number;
    run_id?: string;
    model?: string;
    latency_ms?: number;
    certified_at?: string;
    fixture_names?: string[];
  };
  trigger?: string;
}

export interface FilterOptions {
  discipline?: string;
  minScore?: number;
  query?: string;
}

export interface PillarTabInfo {
  id: 'contract' | 'composition' | 'playbook' | 'score';
  label: string;
  badge: string;
}

export function filterOutcomes(
  outcomes: PublicOutcomeSummary[],
  options: FilterOptions
): PublicOutcomeSummary[] {
  return outcomes.filter((o) => {
    if (options.discipline && options.discipline !== 'All') {
      if ((o.discipline || '').toLowerCase() !== options.discipline.toLowerCase()) {
        return false;
      }
    }
    if (options.minScore !== undefined) {
      const score = o.score?.overall ?? o.quality_score ?? 0;
      if (score < options.minScore) {
        return false;
      }
    }
    if (options.query) {
      const q = options.query.toLowerCase().trim();
      const matchTitle = (o.title || '').toLowerCase().includes(q);
      const matchDiscipline = (o.discipline || '').toLowerCase().includes(q);
      const matchPlaybook = (o.playbook?.overview || '').toLowerCase().includes(q);
      if (!matchTitle && !matchDiscipline && !matchPlaybook) {
        return false;
      }
    }
    return true;
  });
}

export function disciplineTabs(outcomes: PublicOutcomeSummary[]): string[] {
  const set = new Set<string>();
  for (const o of outcomes) {
    if (o.discipline) {
      set.add(o.discipline);
    }
  }
  return ['All', ...Array.from(set).sort()];
}

export function scoreBadgeColor(score: number): 'emerald' | 'amber' | 'slate' {
  if (score >= 10.0) return 'emerald';
  if (score >= 8.0) return 'amber';
  return 'slate';
}

/**
 * Steps come from the composition when the outcome carries one, and otherwise
 * from the flat pipeline the canvas saves. Nodes are the last resort: an outcome
 * drawn on the canvas but never sequenced still has skills worth counting.
 */
export function compositionStepCount(outcome: PublicOutcomeSummary): number {
  return (
    outcome.composition?.pipeline_steps?.length ||
    outcome.pipeline_steps?.length ||
    outcome.composition?.nodes?.length ||
    0
  );
}

/** The skill ids the pipeline runs, in order. */
export function compositionSkillIds(outcome: PublicOutcomeSummary): string[] {
  const source =
    outcome.composition?.pipeline_steps ??
    outcome.pipeline_steps ??
    outcome.composition?.nodes ??
    [];
  return source.map((s) => s.skill_id).filter(Boolean);
}

/** The curated playbook rendered as text, overview first then one rule per line. */
export function playbookText(outcome: PublicOutcomeSummary): string {
  const pb = outcome.playbook;
  if (!pb) return '';
  const parts: string[] = [];
  if (pb.overview) parts.push(pb.overview);
  for (const rule of pb.rules ?? []) parts.push(`- ${rule}`);
  return parts.join('\n');
}

export function requiredContext(outcome: PublicOutcomeSummary): NonNullable<PublicOutcomeSummary['context']> {
  if (outcome.context && outcome.context.length > 0) {
    return outcome.context;
  }
  const derived: NonNullable<PublicOutcomeSummary['context']> = [];
  if (outcome.trigger) {
    derived.push({
      name: outcome.trigger,
      kind: 'trigger',
      description: 'Event that starts this pipeline',
      required: true,
    });
  }
  for (const inp of outcome.contract?.inputs ?? []) {
    derived.push({
      name: inp.name,
      kind: 'input',
      required: true,
    });
  }
  return derived;
}

export function testRigFacts(outcome: PublicOutcomeSummary): Array<{ label: string; value: string }> {
  const rig = outcome.test_rig;
  const facts: Array<{ label: string; value: string }> = [];
  const score = outcome.score?.overall ?? outcome.quality_score;
  if (score !== undefined && score !== null) {
    facts.push({ label: 'Score', value: `${Number(score).toFixed(1)} / 12` });
  }
  if (rig?.epochs) {
    facts.push({ label: 'Training epochs', value: String(rig.epochs) });
  }
  const model = rig?.model || outcome.score?.model_benchmarks?.[0]?.model;
  if (model) {
    facts.push({ label: 'Graded by', value: model });
  }
  const latency = rig?.latency_ms ?? outcome.score?.model_benchmarks?.[0]?.latency_ms;
  if (latency) {
    facts.push({ label: 'Run duration', value: `${Math.round(latency / 1000)}s` });
  }
  const runId = rig?.run_id || outcome.score?.run_id;
  if (runId) {
    facts.push({ label: 'Run', value: runId });
  }
  if (rig?.fixture_names?.length) {
    facts.push({ label: 'Fixtures used', value: rig.fixture_names.join(', ') });
  }
  if (rig?.certified_at) {
    facts.push({ label: 'Certified', value: rig.certified_at.slice(0, 10) });
  }
  return facts;
}

export function outcomePath(outcome: Pick<PublicOutcomeSummary, 'id' | 'slug'>): string {
  return `/outcomes/${outcome.slug || outcome.id}`;
}

export function outcomePageTitle(outcome: PublicOutcomeSummary): string {
  return `${outcome.title} · Sourcetape`;
}

export function outcomePageDescription(outcome: PublicOutcomeSummary): string {
  const raw = outcome.plain_summary || outcome.description || outcome.playbook?.overview || '';
  const text = raw.replace(/\s+/g, ' ').trim();
  if (text) return text.length > 160 ? `${text.slice(0, 159)}...` : text;
  const score = outcome.score?.overall ?? outcome.quality_score ?? 0;
  if (score) return `Certified ${Number(score).toFixed(1)}/12.`;
  return outcome.title || 'Certified outcome';
}

export function outcomeJsonLd(outcome: PublicOutcomeSummary, canonical: string): Record<string, unknown> {
  const origin = canonical.replace(/\/outcomes\/[^/]+$/, '') || 'https://sourcetape.io';
  const summary = outcomePageDescription(outcome);
  const article: Record<string, unknown> = {
    '@type': 'TechArticle',
    headline: outcome.title,
    description: summary,
    url: canonical,
    inLanguage: 'en',
    isAccessibleForFree: true,
    author: { '@type': 'Organization', name: 'Sourcetape', url: origin },
    publisher: { '@type': 'Organization', name: 'Sourcetape', url: origin },
  };
  if (outcome.discipline) {
    article.about = { '@type': 'Thing', name: outcome.discipline };
  }
  if (outcome.test_rig?.certified_at) {
    article.datePublished = outcome.test_rig.certified_at;
  }

  const crumbs: Array<Record<string, unknown>> = [
    { '@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/` },
    { '@type': 'ListItem', position: 2, name: 'Certified outcomes', item: `${origin}/#showcase` },
  ];
  let pos = 3;
  if (outcome.discipline) {
    crumbs.push({ '@type': 'ListItem', position: pos, name: outcome.discipline });
    pos += 1;
  }
  crumbs.push({ '@type': 'ListItem', position: pos, name: outcome.title, item: canonical });

  const graph: Array<Record<string, unknown>> = [
    article,
    { '@type': 'BreadcrumbList', itemListElement: crumbs },
  ];
  const steps = compositionSkillIds(outcome);
  if (steps.length) {
    graph.push({
      '@type': 'HowTo',
      name: outcome.title,
      description: summary,
      step: steps.map((name, index) => ({ '@type': 'HowToStep', position: index + 1, name })),
    });
  }
  return { '@context': 'https://schema.org', '@graph': graph };
}

export function pillarTabs(outcome: PublicOutcomeSummary): PillarTabInfo[] {
  const inCount = outcome.contract?.inputs?.length ?? 0;
  const outCount = outcome.contract?.outputs?.length ?? 0;
  const stepCount = compositionStepCount(outcome);
  const rulesCount = outcome.playbook?.rules?.length ?? 0;
  const scoreVal = (outcome.score?.overall ?? outcome.quality_score ?? 0).toFixed(1);

  return [
    {
      id: 'contract',
      label: 'Contract',
      badge: `${inCount} in / ${outCount} out`,
    },
    {
      id: 'composition',
      label: 'Composition',
      badge: `${stepCount} steps`,
    },
    {
      id: 'playbook',
      label: 'Playbook',
      badge: `${rulesCount} rules`,
    },
    {
      id: 'score',
      label: 'Score',
      badge: `${scoreVal}/12.0`,
    },
  ];
}
